/**
 * Activity Summary Component
 * Displays summary statistics for inventory activity
 */

import React from 'react';
import {
  ClockIcon,
  ChartBarIcon,
  CubeIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';

const ActivitySummary = ({ summary = {}, loading = false }) => {
  const {
    totalActions = 0,
    actionTypes = {},
    itemsAffected = 0,
    recentActivity = 0
  } = summary;

  // Get top action types
  const topActionTypes = Object.entries(actionTypes)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 5);

  // Get action color
  const getActionColor = (action) => {
    switch (action?.toLowerCase()) {
      case 'created':
      case 'added':
        return 'text-green-600 bg-green-50';
      case 'updated':
      case 'modified':
        return 'text-blue-600 bg-blue-50';
      case 'deleted':
      case 'removed':
        return 'text-red-600 bg-red-50';
      case 'status_changed':
      case 'status_update':
        return 'text-purple-600 bg-purple-50';
      case 'maintenance':
        return 'text-yellow-600 bg-yellow-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4"></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-8 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <ChartBarIcon className="h-5 w-5 text-gray-500" />
          <h3 className="text-lg font-medium text-gray-900">Activity Summary</h3>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
          {/* Total Actions */}
          <div className="text-center">
            <div className="flex items-center justify-center h-12 w-12 mx-auto mb-2 bg-blue-50 rounded-lg">
              <ClockIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900">{totalActions}</div>
            <div className="text-sm text-gray-500">Total Actions</div>
          </div>

          {/* Items Affected */}
          <div className="text-center">
            <div className="flex items-center justify-center h-12 w-12 mx-auto mb-2 bg-purple-50 rounded-lg">
              <CubeIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900">{itemsAffected}</div>
            <div className="text-sm text-gray-500">Items Affected</div>
          </div>

          {/* Action Types */}
          <div className="text-center">
            <div className="flex items-center justify-center h-12 w-12 mx-auto mb-2 bg-green-50 rounded-lg">
              <ChartBarIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900">{Object.keys(actionTypes).length}</div>
            <div className="text-sm text-gray-500">Action Types</div>
          </div>

          {/* Recent Activity */}
          <div className="text-center">
            <div className="flex items-center justify-center h-12 w-12 mx-auto mb-2 bg-orange-50 rounded-lg">
              <CalendarDaysIcon className="h-6 w-6 text-orange-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900">{recentActivity}</div>
            <div className="text-sm text-gray-500">Last 24h</div>
          </div>
        </div>

        {/* Top Action Types */}
        {topActionTypes.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Most Common Actions</h4>
            <div className="space-y-2">
              {topActionTypes.map(([action, count]) => {
                const percentage = totalActions > 0 ? ((count / totalActions) * 100).toFixed(1) : 0;
                
                return (
                  <div key={action} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionColor(action)}`}>
                        {action.charAt(0).toUpperCase() + action.slice(1).replace('_', ' ')}
                      </span>
                      <span className="text-sm text-gray-600">{count} times</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-20 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-gray-500 w-10">{percentage}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* No Activity State */}
        {totalActions === 0 && (
          <div className="text-center py-6">
            <ClockIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No inventory activity recorded yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivitySummary;